import { DirectionType } from 'src/components/direction';
import { LoginResult } from 'src/hooks/use-login';
import { MonolithCurrentUserContextValue } from 'src/hooks/use-monolith-user';
import {
  NetworkPointSimple,
  ConnectionPointType,
  BundlingSituation,
  SalesMethod,
  ProductPeriodType,
  FlowUnit,
} from 'src/types/monolith';
import { labelsToOptions } from 'src/utils/labels-to-options';

type ShipperLoginResult = Omit<LoginResult, 'tsoName'>;
type TsoLoginResult = Omit<LoginResult, 'tsoName'> & { tsoName: string };

export const isTso = (
  auth: MonolithCurrentUserContextValue
): auth is TsoLoginResult => auth?.role === 'TSO_USER';

export const isShipper = (
  auth: MonolithCurrentUserContextValue
): auth is ShipperLoginResult =>
  auth?.role === 'SHIPPER_USER' || auth?.role === 'SHIPPER_ADMIN';

export const userIsOwnerOfNetworkPoint = (
  auth: MonolithCurrentUserContextValue,
  networkPoint: NetworkPointSimple
) =>
  isTso(auth) &&
  (auth.referenceId === networkPoint.tso0Id ||
    (networkPoint.bundled && auth.referenceId === networkPoint.tso1Id));

export const connectionPointTypeLabels: Record<ConnectionPointType, string> = {
  MARKET_TRANSITION_POINT: 'Market Area Interconnection Point',
  BORDER_TRANSITION_POINT: 'Cross-Border Interconnection Point',
  RESERVOIR: 'Storage Interconnection Point',
  GRID_ACCESS_POINT: 'End Consumer Exit Point',
  PRODUCTION: 'Production Entry Point',
  BIOGAS_ENTRY: 'Biogas Entry Point',
  OTHER_NETWORK_POINT: 'Other Network Point',
};

export const bundlingLabels: Record<BundlingSituation, string> = {
  NOT_BUNDLED: 'unbundled',
  PART_OF_BUNDLE: 'part of bundle',
  ONE_TO_ONE_BUNDLE: '1:1',
  ONE_TO_MANY_BUNDLE: '1-to-n',
};

export const salesMethodLabels: Record<SalesMethod, string> = {
  AUCTION: 'Auction',
  FCFS: 'FCFS',
  SECONDARY: 'Secondary',
};

export const salesMethodOptions = labelsToOptions(salesMethodLabels);

export const productPeriodTypeLabels: Record<ProductPeriodType, string> = {
  INTRADAY: 'Within-day',
  DAY: 'Day',
  MONTH: 'Month',
  QUARTER: 'Quarter',
  YEAR: 'Year',
};

export const productPeriodTypeOptions = labelsToOptions(
  productPeriodTypeLabels
);

export const flowUnitLabels: Record<FlowUnit, string> = {
  K_WH_H: 'kWh/h',
  K_WH_D: 'kWh/d',
  M3_H: 'Cm³/h',
};

export const flowUnitOptions = labelsToOptions(flowUnitLabels);

export const directionLabels: Record<DirectionType, string> = {
  ENTRY: 'Entry',
  EXIT: 'Exit',
  BUNDLE: 'Bundle',
};

export const directionOptions = labelsToOptions(directionLabels);
